// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { FreeTextListOrNone } from "../FreeTextListOrNone"
import { useI18n } from "@/i18n"
import type { AnswerDraft } from "../types"

interface Props {
  value: AnswerDraft
  onChange: (patch: AnswerDraft) => void
}

/** Step 7 (wire) — likes/dislikes/never-eat/disliked-combinations, each explicit-list-or-none. */
export function Step7Taste({ value, onChange }: Props) {
  const { t } = useI18n()
  // Raw (possibly-undefined) values are what get rendered — an unanswered
  // field must never look like an explicit "None" was already chosen (item
  // 30). "Committed" (defaulted-to-"none") values are only used to
  // reconstruct the OTHER three fields when writing a patch for the one the
  // user just touched — never for display.
  const likesRaw = value.likes as string[] | "none" | undefined
  const dislikesRaw = value.dislikes as string[] | "none" | undefined
  const neverEatRaw = value.neverEat as string[] | "none" | undefined
  const dislikedCombinationsRaw = value.dislikedCombinations as string[] | "none" | undefined
  const likes = likesRaw ?? "none"
  const dislikes = dislikesRaw ?? "none"
  const neverEat = neverEatRaw ?? "none"
  const dislikedCombinations = dislikedCombinationsRaw ?? "none"

  const patch = (fields: Partial<AnswerDraft>) =>
    onChange({ likes, dislikes, neverEat, dislikedCombinations, ...fields })

  return (
    <div className="flex flex-col gap-5">
      <FreeTextListOrNone
        inputId="ob-likes"
        label={t("onboarding.step7.likesLabel")}
        placeholder={t("onboarding.step7.likesPlaceholder")}
        noneLabel={t("onboarding.step7.noneButton")}
        value={likesRaw}
        onChange={next => patch({ likes: next })}
      />
      <FreeTextListOrNone
        inputId="ob-dislikes"
        label={t("onboarding.step7.dislikesLabel")}
        placeholder={t("onboarding.step7.dislikesPlaceholder")}
        noneLabel={t("onboarding.step7.noneButton")}
        value={dislikesRaw}
        onChange={next => patch({ dislikes: next })}
      />
      <FreeTextListOrNone
        inputId="ob-never-eat"
        label={t("onboarding.step7.neverEatLabel")}
        placeholder={t("onboarding.step7.neverEatPlaceholder")}
        noneLabel={t("onboarding.step7.noneButton")}
        value={neverEatRaw}
        onChange={next => patch({ neverEat: next })}
      />
      <FreeTextListOrNone
        inputId="ob-disliked-combos"
        label={t("onboarding.step7.dislikedCombinationsLabel")}
        placeholder={t("onboarding.step7.dislikedCombinationsPlaceholder")}
        noneLabel={t("onboarding.step7.noneButton")}
        value={dislikedCombinationsRaw}
        onChange={next => patch({ dislikedCombinations: next })}
      />
    </div>
  )
}
